import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  ShieldCheck,
  RefreshCw,
  Trash2,
  Award,
  Edit2,
  Copy,
  SlidersHorizontal,
  CheckCircle2,
  AlertCircle,
  Globe,
  Key,
  Check,
  Eye,
  EyeOff,
  Hash,
  Phone,
  Tag,
  Building2,
  KeyRound,
  Info,
  X,
} from "lucide-react";
import {
  listChannels,
  createChannel,
  syncMetaTemplates,
  deleteChannel,
  testMetaConnection,
  type CurrentUser,
  type ChannelAccount,
  type MetaConnectionTestResult,
} from "../../lib/api";

export function ChannelsView({ token, user }: { token: string; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"all" | "meta_official" | "qr_whatsapp">("meta_official");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<ChannelAccount | null>(null);

  // Form State
  const [channelName, setChannelName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Connection Test State
  const [testStatus, setTestStatus] = useState<"idle" | "testing" | "success" | "error">("idle");
  const [testResult, setTestResult] = useState<MetaConnectionTestResult | null>(null);

  const channelsQuery = useQuery({
    queryKey: ["channels", activeTab, user.tenant_id],
    queryFn: () => listChannels(token, activeTab === "all" ? undefined : activeTab, user.tenant_id),
    refetchInterval: 10_000,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createChannel(
        token,
        {
          type: "meta_official",
          name: channelName.trim(),
          phone_number: phoneNumber.trim(),
          waba_id: wabaId.trim(),
          phone_number_id: phoneNumberId.trim(),
          access_token: accessToken.trim(),
        },
        user.tenant_id,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["channels"] });
      closeModal();
    },
  });

  const syncTemplatesMutation = useMutation({
    mutationFn: (channelId: string) => syncMetaTemplates(token, channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns", "templates"] });
      alert("Meta approved templates synced successfully!");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (channelId: string) => deleteChannel(token, channelId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["channels"] }),
  });

  function openCreate() {
    setEditingChannel(null);
    setChannelName("");
    setPhoneNumber("");
    setWabaId("");
    setPhoneNumberId("");
    setAccessToken("");
    setShowToken(false);
    setTestStatus("idle");
    setTestResult(null);
    setIsModalOpen(true);
  }

  function openEdit(channel: ChannelAccount) {
    setEditingChannel(channel);
    setChannelName(channel.name);
    setPhoneNumber(channel.phone_number || "");
    setWabaId(channel.waba_id || "");
    setPhoneNumberId(channel.phone_number_id || "");
    setAccessToken("");
    setShowToken(false);
    setTestStatus("idle");
    setTestResult(null);
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    setEditingChannel(null);
    setShowToken(false);
    setTestStatus("idle");
    setTestResult(null);
  }

  function copyText(text: string, fieldName?: string) {
    navigator.clipboard.writeText(text);
    if (fieldName) {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    }
  }

  async function handleTestConnection() {
    if (!phoneNumberId.trim() || !accessToken.trim()) {
      alert("Please enter Phone Number ID and Access Token to test connection.");
      return;
    }
    setTestStatus("testing");
    setTestResult(null);
    try {
      const res = await testMetaConnection(token, {
        phone_number_id: phoneNumberId.trim(),
        access_token: accessToken.trim(),
      });
      setTestResult(res);
      if (res.status === "success") {
        setTestStatus("success");
      } else {
        setTestStatus("error");
      }
    } catch (err: any) {
      setTestStatus("error");
      setTestResult({
        status: "error",
        error: err.message || "Failed to connect to Meta Cloud API",
      });
    }
  }

  const channels = channelsQuery.data || [];

  const filteredChannels = channels.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.phone_number || "").includes(searchQuery) ||
    (c.waba_id || "").includes(searchQuery)
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-blue-600" />
            Meta Verified Official Channels
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Manage your Meta WhatsApp Business Accounts (WABA), Green Tick Verified numbers, and Cloud API tokens.
          </p>
        </div>

        <button
          onClick={openCreate}
          className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add Meta Verified WABA</span>
        </button>
      </div>

      {/* Meta Webhook Integration Banner */}
      <div className="bg-gradient-to-r from-blue-50/80 via-indigo-50/50 to-slate-50 border border-blue-200/90 rounded-xl p-4 shadow-2xs">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
              </span>
              <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900">
                Meta Cloud API Webhook Integration
              </h3>
            </div>
            <p className="text-xs text-slate-600 max-w-xl">
              Configure this Callback URL &amp; Verify Token in <span className="font-semibold text-slate-800">Meta Developer Portal &gt; WhatsApp &gt; Configuration &gt; Webhook</span> to receive real-time customer replies and delivery ticks. Subscribe to <code className="bg-blue-100/70 text-blue-900 px-1 py-0.5 rounded font-mono text-[11px]">messages</code>.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 px-2.5 py-1.5 rounded-lg shadow-2xs">
              <Globe className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span className="text-slate-500 font-medium">Callback URL:</span>
              <code className="font-mono text-[11px] text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded max-w-[210px] truncate" title="https://wa.justdomediaservices.info/api/v1/channels/meta/webhook">
                https://wa.justdomediaservices.info/api/v1/channels/meta/webhook
              </code>
              <button
                onClick={() => copyText("https://wa.justdomediaservices.info/api/v1/channels/meta/webhook", "url")}
                className="hover:text-blue-600 text-slate-400 ml-1 p-0.5"
                title="Copy Webhook Callback URL"
              >
                {copiedField === "url" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>

            <div className="flex items-center gap-1.5 bg-white border border-slate-200 px-2.5 py-1.5 rounded-lg shadow-2xs">
              <Key className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <span className="text-slate-500 font-medium">Verify Token:</span>
              <code className="font-mono text-[11px] text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded">
                wamaster_meta_verify_2026
              </code>
              <button
                onClick={() => copyText("wamaster_meta_verify_2026", "token")}
                className="hover:text-blue-600 text-slate-400 ml-1 p-0.5"
                title="Copy Verify Token"
              >
                {copiedField === "token" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-72">
            <input
              type="text"
              placeholder="Search by name, phone, WABA ID..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button
            onClick={() => setActiveTab("meta_official")}
            className={`filter-chip ${activeTab === "meta_official" ? "border-blue-500 text-blue-700 bg-blue-50" : ""}`}
          >
            <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
            <span>Official Meta WABA ({channels.filter(c => c.type === "meta_official").length})</span>
          </button>

          <button
            onClick={() => setActiveTab("all")}
            className={`filter-chip ${activeTab === "all" ? "border-slate-400 text-slate-800" : ""}`}
          >
            <span>All Channels ({channels.length})</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => channelsQuery.refetch()}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
            title="Refresh database state"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${channelsQuery.isFetching ? "animate-spin" : ""}`} />
            <span>Sync</span>
          </button>

          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>View</span>
          </button>
        </div>
      </div>

      {/* Channels Grid / Table */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {channelsQuery.isLoading ? (
          <div className="col-span-3 p-12 text-center text-slate-400 text-xs bg-white border border-slate-200 rounded-xl">
            Loading Meta Verified channels...
          </div>
        ) : filteredChannels.length === 0 ? (
          <div className="col-span-3 p-12 text-center bg-white border border-slate-200 rounded-xl shadow-2xs space-y-3">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto text-blue-600">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="text-sm font-bold text-slate-900">No Meta Verified Channels configured yet</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Connect your Official WhatsApp Cloud API account using your Meta WABA ID, Phone Number ID, and System User Access Token.
            </p>
            <button onClick={openCreate} className="btn-primary text-xs py-2 px-3.5 mx-auto inline-flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              <span>Connect Meta Account</span>
            </button>
          </div>
        ) : (
          filteredChannels.map((channel) => (
            <div
              key={channel.id}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs flex flex-col justify-between hover:border-slate-300 transition-all space-y-4"
              >
                <div>
                  {/* Card Top Title & Status */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600 border border-blue-200">
                        <ShieldCheck className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                          {channel.name}
                          <span title="Meta Verified WABA">
                            <Award className="w-4 h-4 text-blue-600" />
                          </span>
                        </h3>
                        <p className="text-xs font-mono font-semibold text-slate-700 mt-0.5">
                          {channel.phone_number || channel.identifier}
                        </p>
                      </div>
                    </div>

                    <span
                      className={`status-pill ${
                        channel.status === "active" ? "status-pill--connected" : "status-pill--disconnected"
                      }`}
                    >
                      {channel.status}
                    </span>
                  </div>

                  {/* Channel Metadata Box */}
                  <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2 text-xs">
                    <div className="flex justify-between items-center text-slate-500">
                      <span>Quality Rating:</span>
                      <span className="font-bold text-emerald-600 flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        {channel.quality_score || "GREEN (High)"}
                      </span>
                    </div>

                    <div className="flex justify-between items-center text-slate-500">
                      <span>Messaging Tier:</span>
                      <span className="font-mono text-slate-800 font-bold">{channel.messaging_tier || "TIER_10K"}</span>
                    </div>

                    <div className="flex justify-between items-center text-slate-500">
                      <span>WABA ID:</span>
                      <div className="flex items-center gap-1 font-mono text-slate-700">
                        <span>{channel.waba_id || "10987654321"}</span>
                        <button onClick={() => copyText(channel.waba_id || "")} className="hover:text-slate-900">
                          <Copy className="w-3 h-3 text-slate-400" />
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-slate-500">
                      <span>Phone Number ID:</span>
                      <div className="flex items-center gap-1 font-mono text-slate-700">
                        <span>{channel.phone_number_id || "20987654321"}</span>
                        <button onClick={() => copyText(channel.phone_number_id || "")} className="hover:text-slate-900">
                          <Copy className="w-3 h-3 text-slate-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Actions */}
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  <button
                    className="btn-secondary text-xs py-1.5 px-2.5 flex items-center gap-1.5 flex-1 shadow-2xs"
                    onClick={() => syncTemplatesMutation.mutate(channel.id)}
                    disabled={syncTemplatesMutation.isPending}
                    title="Pull pre-approved templates from Meta Graph API"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 text-blue-600 ${syncTemplatesMutation.isPending ? "animate-spin" : ""}`} />
                    <span>Sync Templates</span>
                  </button>

                  <button
                    onClick={() => openEdit(channel)}
                    className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors"
                    title="Edit Credentials"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>

                  <button
                    className="p-1.5 rounded-lg border border-rose-200 hover:bg-rose-50 text-rose-500 hover:text-rose-700 transition-colors"
                    onClick={() => {
                      if (confirm(`Delete Meta Channel "${channel.name}"?`)) {
                        deleteMutation.mutate(channel.id);
                      }
                    }}
                    title="Delete Channel"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
        )}
      </div>

      {/* Add / Edit Meta Verified Channel Modal */}
      {isModalOpen && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal-card max-w-lg" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3.5 border-b border-slate-100 mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shadow-2xs">
                  <ShieldCheck className="w-4.5 h-4.5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 leading-none">
                    {editingChannel ? `Edit Meta Channel: ${editingChannel.name}` : "Connect Meta Official WABA"}
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Official WhatsApp Business Account with Graph API v21.0
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3.5">
              {/* Channel Display Name */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Channel Display Name <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="e.g. Official Support Desk"
                    className="w-full text-xs font-medium pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all shadow-2xs"
                    value={channelName}
                    onChange={(e) => setChannelName(e.target.value)}
                  />
                </div>
              </div>

              {/* WhatsApp Phone Number */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  WhatsApp Phone Number (with Country Code) <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="+919323818985"
                    className="w-full text-xs font-mono font-medium pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all shadow-2xs"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </div>
              </div>

              {/* 2-Column Grid with perfectly aligned labels */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5 truncate" title="WhatsApp Business Account (WABA) ID">
                    WABA Account ID <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="e.g. 109876543210987"
                      className="w-full text-xs font-mono font-medium pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all shadow-2xs"
                      value={wabaId}
                      onChange={(e) => setWabaId(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5 truncate" title="Meta Phone Number ID">
                    Phone Number ID <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="e.g. 209876543210987"
                      className="w-full text-xs font-mono font-medium pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all shadow-2xs"
                      value={phoneNumberId}
                      onChange={(e) => setPhoneNumberId(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Meta Permanent System User Access Token with toggle */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Meta Permanent System User Access Token <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type={showToken ? "text" : "password"}
                    placeholder="EAAG... (never expires token)"
                    className="w-full text-xs font-mono font-medium pl-9 pr-9 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all shadow-2xs"
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                    title={showToken ? "Hide token" : "Show token"}
                  >
                    {showToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>

                <div className="flex items-start gap-1.5 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5 mt-2">
                  <Info className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                  <span>
                    Requires a permanent token from <strong>Meta Business Manager &gt; System Users</strong> with <code className="text-blue-700 bg-blue-50 px-1 py-0.5 rounded font-mono text-[10px]">whatsapp_business_messaging</code> permissions.
                  </span>
                </div>
              </div>

              {/* Test Connection Button & Live Validation Result */}
              <div className="pt-1">
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={!phoneNumberId.trim() || !accessToken.trim() || testStatus === "testing"}
                  className="w-full py-2.5 px-4 text-xs font-semibold rounded-lg flex items-center justify-center gap-2 border transition-all shadow-2xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200 active:bg-blue-200"
                >
                  <RefreshCw className={`w-3.5 h-3.5 text-blue-600 ${testStatus === "testing" ? "animate-spin" : ""}`} />
                  <span>{testStatus === "testing" ? "Validating with Meta Graph API..." : "Test Meta Connection"}</span>
                </button>

                {testStatus === "success" && testResult && (
                  <div className="mt-2.5 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs space-y-1 animate-fadeIn">
                    <div className="flex items-center gap-1.5 font-bold text-emerald-700">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span>Meta Connection Valid &amp; Verified!</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] pt-1">
                      <div><span className="text-emerald-950 font-semibold">Verified Name:</span> {testResult.verified_name || "Official Account"}</div>
                      <div><span className="text-emerald-950 font-semibold">Phone:</span> {testResult.display_phone_number || phoneNumber}</div>
                      <div><span className="text-emerald-950 font-semibold">Quality:</span> <span className="font-bold text-emerald-700">{testResult.quality_rating || "GREEN"}</span></div>
                      <div><span className="text-emerald-950 font-semibold">Tier:</span> {testResult.messaging_limit_tier || "TIER_10K"}</div>
                    </div>
                  </div>
                )}

                {testStatus === "error" && testResult && (
                  <div className="mt-2.5 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs space-y-1 animate-fadeIn">
                    <div className="flex items-center gap-1.5 font-bold text-rose-700">
                      <AlertCircle className="w-4 h-4 text-rose-600" />
                      <span>Meta Verification Failed</span>
                    </div>
                    <p className="text-[11px] text-rose-700 font-mono break-all">{testResult.error || "Invalid Credentials or Meta Graph API error."}</p>
                  </div>
                )}
              </div>

              {/* Modal Footer Actions */}
              <div className="flex items-center justify-end gap-2.5 pt-3.5 border-t border-slate-100">
                <button
                  type="button"
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors cursor-pointer"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 rounded-lg shadow-2xs transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 cursor-pointer"
                  onClick={() => createMutation.mutate()}
                  disabled={!channelName.trim() || !phoneNumber.trim() || !wabaId.trim() || !phoneNumberId.trim() || !accessToken.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Connecting...</span>
                    </>
                  ) : editingChannel ? (
                    "Save Changes"
                  ) : (
                    "Save & Verify WABA"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
